import type { Writable } from "svelte/store";
import { FS } from "./fs";
import type { InternetArchiveConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://archive.org/developers/ias3.html
// https://archive.org/developers/md-read.html
// The Internet Archive answers other origins, so the browser calls it directly
// and the keys never reach our server. The calls follow rclone's
// internetarchive backend.
//
// The top folder lists the items of the account. An item is a flat list of
// files; a "/" in a file name makes a folder. A folder without files does not
// exist there, so an empty one we are asked to make is kept in memory.
//
// What is written is queued by the Internet Archive and can take minutes to
// show in the list of an item. Until it does we remember what we stored and
// removed, and list that.

export const iaS3 = "https://s3.us.archive.org";
export const iaFront = "https://archive.org";

export const prop_ia_md5 = "ia_md5";
export const prop_ia_sha1 = "ia_sha1";
export const prop_ia_format = "ia_format";
export const prop_ia_source = "ia_source";

const maxNameLen = 230;
// how long we believe our memory over the list of an item
const pendingMs = 60 * 60 * 1000;

// a file as /metadata/<item> lists it
export interface IAFile {
  name?: string;
  // "original", "derivative" or "metadata"
  source?: string;
  // seconds since epoch
  mtime?: string;
  // set by rclone and by us: RFC 3339
  "rclone-mtime"?: string | string[];
  size?: string;
  md5?: string;
  sha1?: string;
  crc32?: string;
  format?: string;
  // set when the hashes are not of the content
  summation?: string;
}

// an item as the top folder lists it
export interface IAItem {
  name: string;
  // ms since epoch
  created: number;
}

// what we stored (size set) or removed and the list may not show yet
interface Pending {
  size?: number;
  mtime: number;
  at: number;
}

class IAError extends Error {}

function splitPath(path: string): string[] {
  return path.split("/").filter((s) => s != "");
}

function encodePath(parts: string[]): string {
  return parts.map(encodeURIComponent).join("/");
}

// Refuses names that cannot be the identifier of an item.
export function iaCheckItemName(name: string): void {
  throwIf(name == "", "missing name");
  let ok = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(name);
  throwIf(!ok, "the name of an item can have letters, digits, . _ and - only, and starts with a letter or digit");
}

// Refuses the names rclone does not send to the Internet Archive as they are.
export function iaCheckName(name: string): void {
  throwIf(name == "", "missing name");
  throwIf(name.length > maxNameLen, `a name cannot be longer than ${maxNameLen} characters`);
  let bad = /[<>\x00-\x1f\x7f]/.test(name) || name == "." || name == "..";
  throwIf(bad, "a name cannot contain < or >");
}

function fileMtime(f: IAFile): number {
  let rm = f["rclone-mtime"];
  for (let v of Array.isArray(rm) ? rm : rm ? [rm] : []) {
    let ms = Date.parse(v);
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  let secs = Number(f.mtime || 0);
  return Number.isFinite(secs) && secs > 0 ? Math.floor(secs * 1000) : 0;
}

function fileSize(f: IAFile): number {
  let n = Number(f.size || 0);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * The entries of the folder at prefix ("" or ending with "/") of an item:
 * files whose name has the prefix and the folders their names make. dirs are
 * folders that have no files.
 */
export function iaFilesToWire(item: string, prefix: string, files: IAFile[], dirs: string[] = []): any[][] {
  let wires: any[][] = [];
  let seen = new Set<string>();
  let addDir = (name: string) => {
    if (name != "" && !seen.has(name)) {
      seen.add(name);
      wires.push([`${item}/${prefix}${name}`, name, -1, 0, []]);
    }
  };
  for (let f of files) {
    let name = f.name || "";
    if (!name.startsWith(prefix)) {
      continue;
    }
    let rest = name.substring(prefix.length);
    let i = rest.indexOf("/");
    if (i >= 0) {
      addDir(rest.substring(0, i));
      continue;
    }
    if (rest == "") {
      continue;
    }
    let meta: any[] = [`${item}/${name}`, rest, fileSize(f), fileMtime(f), null];
    if (!f.summation) {
      if (f.md5) {
        meta.push(prop_ia_md5, f.md5);
      }
      if (f.sha1) {
        meta.push(prop_ia_sha1, f.sha1);
      }
    }
    if (f.format) {
      meta.push(prop_ia_format, f.format);
    }
    if (f.source) {
      meta.push(prop_ia_source, f.source);
    }
    wires.push(meta);
  }
  for (let d of dirs) {
    if ((d + "/").startsWith(prefix)) {
      addDir(d.substring(prefix.length).split("/")[0]);
    }
  }
  return wires;
}

export function iaItemsToWire(items: IAItem[]): any[][] {
  return items.map((it) => [it.name, it.name, -1, it.created, []]);
}

// The items in the answer to a list of buckets. Identifiers have no
// characters that XML escapes.
export function iaParseItems(xml: string): IAItem[] {
  let items: IAItem[] = [];
  for (let m of xml.matchAll(/<Bucket>([\s\S]*?)<\/Bucket>/g)) {
    let name = /<Name>([^<]+)<\/Name>/.exec(m[1])?.[1].trim();
    if (!name) {
      continue;
    }
    let created = Date.parse(/<CreationDate>([^<]+)<\/CreationDate>/.exec(m[1])?.[1].trim() || "");
    items.push({ name, created: Number.isFinite(created) ? created : 0 });
  }
  return items;
}

class FileSystemInternetArchive extends FS {
  supportsDirRename = true;

  // by item/name
  pending = new Map<string, Pending>();
  // items we made, by name, with the time we made them
  madeItems = new Map<string, number>();
  // folders without files, as item/path
  emptyDirs = new Set<string>();

  constructor(config: InternetArchiveConfig) {
    super(config);
  }

  get auth(): string {
    let c = this.config as InternetArchiveConfig;
    return iaAuth(c.accessKey, c.secretKey);
  }

  call(method: string, url: string, headers: Record<string, string> = {}): Promise<string> {
    return iaCall(this.auth, method, url, headers);
  }

  async listItems(): Promise<IAItem[]> {
    let items = iaParseItems(await this.call("GET", iaS3 + "/"));
    let now = Date.now();
    for (let [name, at] of this.madeItems) {
      if (items.some((it) => it.name == name) || now - at > pendingMs) {
        this.madeItems.delete(name);
      } else {
        items.push({ name, created: at });
      }
    }
    return items;
  }

  // The files of an item as the Internet Archive lists them.
  async listStored(item: string): Promise<IAFile[]> {
    iaCheckItemName(item);
    let text = await this.call("GET", `${iaFront}/metadata/${item}`);
    let js = parseJSON(text);
    throwIf(typeof js.error == "string" && js.error != "", `Internet Archive: ${String(js.error).substring(0, 200)}`);
    // an item that is not there, or is being made, is {}
    let files = Array.isArray(js.files) ? (js.files as IAFile[]) : [];
    return files.filter((f) => f && typeof f.name == "string" && f.name != "");
  }

  // The files of an item with what we stored and removed since.
  async listFiles(item: string): Promise<IAFile[]> {
    let files = await this.listStored(item);
    let now = Date.now();
    let prefix = item + "/";
    for (let [key, p] of this.pending) {
      if (!key.startsWith(prefix)) {
        continue;
      }
      let name = key.substring(prefix.length);
      let i = files.findIndex((f) => f.name == name);
      let shown =
        p.size === undefined ? i < 0 : i >= 0 && fileSize(files[i]) == p.size && fileMtime(files[i]) == p.mtime;
      if (shown || now - p.at > pendingMs) {
        this.pending.delete(key);
        continue;
      }
      if (i >= 0) {
        files.splice(i, 1);
      }
      if (p.size !== undefined) {
        files.push({
          name,
          size: String(p.size),
          "rclone-mtime": new Date(p.mtime).toISOString(),
        });
      }
    }
    return files;
  }

  // the folders without files of an item, as paths in it
  emptyDirsOf(item: string): string[] {
    let prefix = item + "/";
    return [...this.emptyDirs].filter((d) => d.startsWith(prefix)).map((d) => d.substring(prefix.length));
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parts = splitPath(dirPath);
      if (parts.length == 0) {
        return FsDir.fromWire(dirPath, iaItemsToWire(await this.listItems()));
      }
      let item = parts[0];
      let prefix = parts.length > 1 ? parts.slice(1).join("/") + "/" : "";
      let files = await this.listFiles(item);
      let wires = iaFilesToWire(item, prefix, files, this.emptyDirsOf(item));
      let exists = prefix == "" || wires.length > 0 || this.emptyDirs.has(parts.join("/"));
      throwIf(!exists, `folder not found: ${dirPath}`);
      return FsDir.fromWire(dirPath, wires);
    });
  }

  // Splits the path of a file or a folder in an item.
  inItem(path: string, what: string): { item: string; name: string; parts: string[] } {
    let parts = splitPath(path);
    throwIf(parts.length == 0, `missing ${what}`);
    throwIf(parts.length == 1, "the top folder has items only: files go in an item");
    iaCheckItemName(parts[0]);
    return { item: parts[0], name: parts.slice(1).join("/"), parts };
  }

  // For preview and for a link: it works for the items everybody can read.
  async getPublicURL(path: string): Promise<string> {
    let p = this.inItem(path, "file name");
    return `${iaFront}/download/${encodePath(p.parts)}`;
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let p = this.inItem(path, "file name");
    // /download/ sends us to a server that does not answer other origins
    let xhr = await iaSend("GET", `${iaFront}/cors/${encodePath(p.parts)}`, this.auth, {}, null, "blob", httpProgress);
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    throwIf(path.endsWith("/"), "missing file name");
    let p = this.inItem(path, "file name");
    p.parts.slice(1).forEach(iaCheckName);
    let mtime = file instanceof File ? file.lastModified : Date.now();
    let headers: Record<string, string> = {
      // as in rclone: makes the item when it is not there
      "x-amz-auto-make-bucket": "1",
      "x-archive-auto-make-bucket": "1",
      "x-archive-keep-old-version": "0",
      "x-archive-queue-derive": "0",
      "x-archive-size-hint": String(file.size),
      // where rclone keeps the time of a file
      "x-amz-filemeta-rclone-mtime": new Date(mtime).toISOString(),
    };
    await iaSend("PUT", `${iaS3}/${encodePath(p.parts)}`, this.auth, headers, file, "text", (loaded, _total, durMs) => {
      httpProgress(Math.min(loaded, file.size), file.size, durMs);
    });
    this.pending.set(`${p.item}/${p.name}`, { size: file.size, mtime, at: Date.now() });
  }

  async copyFile(item: string, f: IAFile, toItem: string, toName: string): Promise<void> {
    let mtime = fileMtime(f);
    let headers: Record<string, string> = {
      "x-archive-auto-make-bucket": "1",
      "x-archive-queue-derive": "0",
      "x-archive-keep-old-version": "0",
      "x-amz-copy-source": "/" + encodePath([item, ...f.name!.split("/")]),
      "x-amz-metadata-directive": "COPY",
      "x-archive-filemeta-size": String(fileSize(f)),
      "x-archive-filemeta-rclone-mtime": new Date(mtime).toISOString(),
    };
    for (let k of ["sha1", "md5", "crc32"] as const) {
      if (f[k] && !f.summation) {
        headers[`x-archive-filemeta-${k}`] = f[k]!;
      }
    }
    await this.call("PUT", `${iaS3}/${encodePath([toItem, ...toName.split("/")])}`, headers);
    this.pending.set(`${toItem}/${toName}`, { size: fileSize(f), mtime, at: Date.now() });
  }

  async removeFile(item: string, name: string): Promise<void> {
    await this.call("DELETE", `${iaS3}/${encodePath([item, ...name.split("/")])}`, {
      // as in rclone: no copy is kept, and what was made of the file goes too
      "x-archive-keep-old-version": "0",
      "x-archive-cascade-delete": "1",
    });
    this.pending.set(`${item}/${name}`, { mtime: 0, at: Date.now() });
  }

  // The Internet Archive cannot rename: a file is copied and then removed.
  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let isDir = path.endsWith("/");
    throwIf(splitPath(path).length == 1 || splitPath(newPath).length == 1, "an item cannot be renamed or moved");
    let from = this.inItem(path, "name");
    let to = this.inItem(newPath, "name");
    to.parts.slice(1).forEach(iaCheckName);

    let src = await this.listFiles(from.item);
    let dst = from.item == to.item ? src : await this.listFiles(to.item);
    let toKey = `${to.item}/${to.name}`;
    let taken = dst.some((f) => f.name == to.name || f.name!.startsWith(to.name + "/")) || this.emptyDirs.has(toKey);
    throwIf(taken, `${to.parts[to.parts.length - 1]} already exists`);

    if (!isDir) {
      let f = src.find((f) => f.name == from.name);
      throwIf(!f, `file not found: ${path}`);
      await this.moveFile(from.item, f!, to.item, to.name);
      return;
    }
    let fromKey = `${from.item}/${from.name}`;
    throwIf((toKey + "/").startsWith(fromKey + "/"), "cannot move a folder into itself");
    let prefix = from.name + "/";
    let files = src.filter((f) => f.name!.startsWith(prefix));
    let empty = [...this.emptyDirs].filter((d) => d == fromKey || d.startsWith(fromKey + "/"));
    throwIf(files.length == 0 && empty.length == 0, `folder not found: ${path}`);
    for (let f of files) {
      await this.moveFile(from.item, f, to.item, to.name + "/" + f.name!.substring(prefix.length));
    }
    for (let d of empty) {
      this.emptyDirs.delete(d);
      this.emptyDirs.add(toKey + d.substring(fromKey.length));
    }
  }

  async moveFile(item: string, f: IAFile, toItem: string, toName: string): Promise<void> {
    let stored = !this.pending.has(`${item}/${f.name}`);
    throwIf(!stored, `the Internet Archive is still storing ${f.name}; try again later`);
    await this.copyFile(item, f, toItem, toName);
    await this.removeFile(item, f.name!);
  }

  async createDirectory(dirPath: string): Promise<void> {
    let parts = splitPath(dirPath);
    throwIf(parts.length == 0, "missing directory name");
    iaCheckItemName(parts[0]);
    if (parts.length > 1) {
      parts.slice(1).forEach(iaCheckName);
      this.emptyDirs.add(parts.join("/"));
      return;
    }
    let item = parts[0];
    let items = await this.listItems();
    throwIf(
      items.some((it) => it.name == item),
      `${item} already exists`,
    );
    await this.call("PUT", `${iaS3}/${item}`, { "x-archive-queue-derive": "0" });
    this.madeItems.set(item, Date.now());
  }

  // A folder goes with every file in it. An item stays: the Internet Archive
  // lets nobody but its staff remove one.
  async deleteFile(path: string): Promise<void> {
    throwIf(splitPath(path).length == 1, "an item cannot be removed here: the Internet Archive keeps items");
    let p = this.inItem(path, "name");
    let files = await this.listFiles(p.item);
    if (!path.endsWith("/")) {
      let f = files.find((f) => f.name == p.name);
      throwIf(!f, `file not found: ${path}`);
      await this.removeFile(p.item, p.name);
      return;
    }
    let key = `${p.item}/${p.name}`;
    let prefix = p.name + "/";
    let inside = files.filter((f) => f.name!.startsWith(prefix));
    let empty = [...this.emptyDirs].filter((d) => d == key || d.startsWith(key + "/"));
    throwIf(inside.length == 0 && empty.length == 0, `folder not found: ${path}`);
    for (let f of inside) {
      await this.removeFile(p.item, f.name!);
    }
    for (let d of empty) {
      this.emptyDirs.delete(d);
    }
  }
}

function iaAuth(accessKey: string, secretKey: string): string {
  return `LOW ${accessKey}:${secretKey}`;
}

/**
 * Checks the keys. Returns the name of their account, "" when the Internet
 * Archive does not say.
 */
export async function iaConnect(accessKey: string, secretKey: string): Promise<string> {
  let text = await iaCall(iaAuth(accessKey, secretKey), "GET", `${iaS3}/?check_auth=1`);
  let js = parseJSON(text);
  throwIf(js.authorized !== true, "the Internet Archive refused the keys");
  for (let k of ["username", "screenname", "itemname"]) {
    if (typeof js[k] == "string" && js[k] != "") {
      return js[k];
    }
  }
  return "";
}

function parseJSON(text: string): any {
  try {
    let js = JSON.parse(text);
    return js && typeof js == "object" ? js : {};
  } catch {
    return {};
  }
}

// What an answer that is not 2xx says. The S3 side answers in XML, the rest
// in JSON. Nothing of the request is quoted.
function iaError(status: number, text: string): IAError {
  let code = /<Code>([^<]*)<\/Code>/.exec(text)?.[1] || "";
  let msg = /<Message>([^<]*)<\/Message>/.exec(text)?.[1] || "";
  if (msg == "") {
    let js = parseJSON(text);
    msg = typeof js.error == "string" ? js.error : "";
  }
  msg = msg.trim().substring(0, 200);
  if (code == "InvalidAccessKeyId" || code == "SignatureDoesNotMatch" || status == 401) {
    return new IAError("the Internet Archive refused the keys");
  }
  if (code == "SlowDown" || status == 503 || status == 429) {
    return new IAError("the Internet Archive is busy; try again later");
  }
  if (status == 404) {
    return new IAError(msg ? `Internet Archive: ${msg}` : "not found at the Internet Archive");
  }
  return new IAError(msg ? `Internet Archive: ${msg}` : `Internet Archive ${status}`);
}

async function iaCall(
  auth: string,
  method: string,
  url: string,
  headers: Record<string, string> = {},
): Promise<string> {
  let rsp: Response;
  try {
    rsp = await fetch(url, { method, headers: { ...headers, Authorization: auth } });
  } catch {
    throw new Error("could not connect to the Internet Archive");
  }
  let text = await rsp.text();
  if (!rsp.ok) {
    throw iaError(rsp.status, text);
  }
  return text;
}

// XMLHttpRequest because fetch does not report how much was sent.
function iaSend(
  method: string,
  url: string,
  auth: string,
  headers: Record<string, string>,
  body: Blob | null,
  responseType: "text" | "blob",
  onProgress: (loaded: number, total: number, durMs: number) => void,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let started = Date.now();
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.responseType = responseType;
    xhr.setRequestHeader("Authorization", auth);
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      reject(iaError(xhr.status, responseType == "text" ? xhr.responseText : ""));
    };
    xhr.onerror = () => {
      reject(
        new Error(
          method == "GET" ? "download from the Internet Archive failed" : "upload to the Internet Archive failed",
        ),
      );
    };
    let report = (ev: ProgressEvent) => {
      onProgress(ev.loaded, ev.lengthComputable ? ev.total : 0, Date.now() - started);
    };
    if (body) {
      xhr.upload.onprogress = report;
    } else {
      xhr.onprogress = report;
    }
    xhr.send(body);
  });
}

export function makeFileSystemInternetArchive(c: InternetArchiveConfig): FileSystemInternetArchive {
  return new FileSystemInternetArchive(c);
}
